import React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
interface InternalLinkProps {
  to: string;
  children: React.ReactNode;
  className?: string;
  title?: string;
  prefetch?: boolean;
  keywords?: string[];
}

// Strategic internal linking component with SEO optimization
const InternalLink: React.FC<InternalLinkProps> = ({
  to,
  children,
  className,
  title,
  prefetch = true,
  keywords = []
}) => {
  // Add hover effect for preloading on hover
  const handleMouseEnter = () => {
    if (prefetch && 'requestIdleCallback' in window) {
      requestIdleCallback(() => {
        // Preload the route component
        import(/* webpackChunkName: "[request]" */`../pages${to === '/' ? '/Index' : to.split('/').map(segment => segment.charAt(0).toUpperCase() + segment.slice(1)).join('')}`).catch(() => {
          // Silently fail for dynamic routes
        });
      });
    }
  };

  // Generate descriptive title if not provided
  const getLinkTitle = () => {
    if (title) return title;
    const titleMap: Record<string, string> = {
      '/': 'Visit Mappa Caeli homepage for Catholic education',
      '/quest-map': 'Start interactive Catholic learning quests',
      '/about': 'About Mappa Caeli Catholic education mission',
      '/pricing': 'View Catholic education pricing plans',
      '/login': 'Login to your Catholic education account',
      '/signup': 'Sign up for Catholic family education'
    };
    return titleMap[to] || `Navigate to ${to}`;
  };
  return <Link to={to} className={cn("transition-colors duration-200 hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2", className)} title={getLinkTitle()} onMouseEnter={handleMouseEnter} data-keywords={keywords.join(',')}>
      {children}
    </Link>;
};

// Context-aware breadcrumb component for better internal linking
export const ContextualBreadcrumbs: React.FC<{
  path: string;
}> = ({
  path
}) => {
  const segments = path.split('/').filter(Boolean);
  const getBreadcrumbData = (segment: string, index: number) => {
    const breadcrumbMap: Record<string, {
      label: string;
      keywords: string[];
    }> = {
      'quest-map': {
        label: 'Quest Map',
        keywords: ['catholic quests', 'interactive learning']
      },
      'about': {
        label: 'About Us',
        keywords: ['catholic family', 'mappa caeli']
      },
      'lesson': {
        label: 'Lesson',
        keywords: ['catholic lesson', 'religious education']
      },
      'quiz': {
        label: 'Quiz',
        keywords: ['catholic quiz', 'catechism test']
      }
    };
    return breadcrumbMap[segment] || {
      label: segment,
      keywords: []
    };
  };
  return <nav aria-label="Breadcrumb" className="text-sm text-muted-foreground mb-4">
      <ol className="flex items-center space-x-2">
        <li>
          <InternalLink to="/" className="hover:text-primary" keywords={['mappa caeli', 'catholic education', 'home']}>
            Home
          </InternalLink>
        </li>
        {segments.map((segment, index) => {
        const {
          label,
          keywords
        } = getBreadcrumbData(segment, index);
        const path = '/' + segments.slice(0, index + 1).join('/');
        return <React.Fragment key={segment}>
              <li className="text-muted-foreground">→</li>
              <li>
                {index === segments.length - 1 ? <span className="text-foreground font-medium">{label}</span> : <InternalLink to={path} className="hover:text-primary" keywords={keywords}>
                    {label}
                  </InternalLink>}
              </li>
            </React.Fragment>;
      })}
      </ol>
    </nav>;
};

// Related content suggestions component for cross-linking
export const RelatedContent: React.FC<{
  currentPath: string;
}> = ({
  currentPath
}) => {
  const getRelatedLinks = (path: string) => {
    const related: Record<string, Array<{
      to: string;
      title: string;
      description: string;
      keywords: string[];
    }>> = {
      '/': [{
        to: '/quest-map',
        title: 'Explore Interactive Quests',
        description: 'Gamified Catholic learning adventures',
        keywords: ['catholic games', 'interactive learning', 'religious quests']
      }],
      '/quest-map': [{
        to: '/pilgrims-arcade',
        title: 'Play Catholic Games',
        description: 'Fun educational games for the whole family',
        keywords: ['catholic games', 'interactive learning', 'family games']
      }]
    };
    return related[path] || [];
  };
  const relatedLinks = getRelatedLinks(currentPath);
  if (relatedLinks.length === 0) return null;
  return;
};
export default InternalLink;
